import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CrmUpgrade from "./CrmUpgrade";
import {
  crmFetch, stageInfo, activityInfo, ACTIVITY_TYPES,
  formatMoney, formatDate, formatDateTime,
} from "./crmApi";
import "./Crm.css";

const PAGE_SIZE = 25;

const EMPTY_FORM = {
  name: "", phone: "", email: "", city: "", address: "", source: "", notes: "",
};

/**
 * The client book: everyone who ever enquired or booked, searchable, with a
 * detail drawer showing their leads, timeline and notes.
 */
function CrmClients() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);

  const [modalClient, setModalClient] = useState(null); // null = closed, {} = new, {...} = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [actType, setActType] = useState("note");
  const [actSummary, setActSummary] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      if (search.trim()) params.set("search", search.trim());
      const d = await crmFetch(`/api/crm/clients?${params}`);
      setClients(d.clients || []);
      setTotal(d.total || 0);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate, page, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const openDetail = async (id) => {
    try {
      setDetail(await crmFetch(`/api/crm/clients/${id}`));
    } catch { /* row click is retryable */ }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setModalClient({});
  };

  const openEdit = (client) => {
    setForm({
      name: client.name || "", phone: client.phone || "", email: client.email || "",
      city: client.city || "", address: client.address || "",
      source: client.source || "", notes: client.notes || "",
    });
    setFormError("");
    setModalClient(client);
  };

  const saveClient = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError("Name is required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (modalClient.id) {
        await crmFetch(`/api/crm/clients/${modalClient.id}`, { method: "PUT", body: form });
        if (detail?.client?.id === modalClient.id) openDetail(modalClient.id);
      } else {
        await crmFetch("/api/crm/clients", { method: "POST", body: form });
      }
      setModalClient(null);
      load();
    } catch (err) {
      setFormError(err.message);
    }
    setSaving(false);
  };

  const deleteClient = async (client) => {
    if (!window.confirm(
      `Delete ${client.name}? Their leads and activity history go too. This cannot be undone.`
    )) return;
    try {
      await crmFetch(`/api/crm/clients/${client.id}`, { method: "DELETE" });
      setDetail(null);
      load();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!actSummary.trim() || !detail) return;
    try {
      await crmFetch("/api/crm/activities", {
        method: "POST",
        body: { client_id: detail.client.id, type: actType, summary: actSummary },
      });
      setActSummary("");
      openDetail(detail.client.id);
    } catch (err) {
      window.alert(err.message);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="crm-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="crm-main">
        <header className="crm-header">
          <div className="crm-header-left">
            <button className="crm-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div>
              <h2>👥 Clients</h2>
              <p className="crm-header-sub">Everyone who has enquired or booked with your studio</p>
            </div>
          </div>
          <button className="crm-primary-btn" onClick={openNew}>＋ Add Client</button>
        </header>

        <div className="crm-content">
          {planBlocked ? <CrmUpgrade /> : (
          <>
          <div className="crm-board-tools">
            <input
              className="crm-search"
              placeholder="🔍 Search name, phone, email or city…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </div>

          {loading ? (
            <div className="crm-loading">Loading clients…</div>
          ) : clients.length === 0 ? (
            <div className="crm-empty">
              <span className="crm-empty-icon">👥</span>
              {search ? "No clients match that search." : "No clients yet."}
              {!search && (
                <div className="crm-empty-hint">
                  Clients are added automatically when you create a lead, or add one directly.
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="crm-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Contact</th>
                      <th>City</th>
                      <th>Leads</th>
                      <th>Booked</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id} onClick={() => openDetail(c.id)}>
                        <td>
                          <div className="cell-main">{c.name}</div>
                          {c.source && <div className="cell-sub">via {c.source}</div>}
                        </td>
                        <td>
                          {c.phone && <div>📱 {c.phone}</div>}
                          {c.email && <div className="cell-sub">{c.email}</div>}
                          {!c.phone && !c.email && "—"}
                        </td>
                        <td>{c.city || "—"}</td>
                        <td>{c.lead_count}</td>
                        <td>{c.booked_count > 0 ? `✅ ${c.booked_count}` : "—"}</td>
                        <td>{formatDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pages > 1 && (
                  <div className="crm-pager">
                    <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
                    <span>Page {page + 1} of {pages}</span>
                    <button disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next →</button>
                  </div>
                )}
              </div>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {modalClient !== null && (
        <div className="crm-modal-backdrop" onClick={() => setModalClient(null)}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modalClient.id ? "Edit client" : "＋ Add client"}</h3>
            {formError && <div className="crm-form-error">{formError}</div>}
            <form onSubmit={saveClient}>
              <div className="crm-form-grid">
                <div className="crm-field">
                  <label>Name *</label>
                  <input value={form.name} onChange={set("name")} autoFocus />
                </div>
                <div className="crm-field">
                  <label>Phone</label>
                  <input value={form.phone} onChange={set("phone")} />
                </div>
                <div className="crm-field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={set("email")} />
                </div>
                <div className="crm-field">
                  <label>City</label>
                  <input value={form.city} onChange={set("city")} />
                </div>
                <div className="crm-field full">
                  <label>Address</label>
                  <input value={form.address} onChange={set("address")} />
                </div>
                <div className="crm-field full">
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={set("notes")} />
                </div>
              </div>
              <div className="crm-modal-actions">
                <button type="button" className="crm-btn ghost" onClick={() => setModalClient(null)}>Cancel</button>
                <button type="submit" className="crm-btn primary" disabled={saving}>
                  {saving ? "Saving…" : modalClient.id ? "Save changes" : "Add client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && (
        <>
          <div className="crm-drawer-backdrop" onClick={() => setDetail(null)} />
          <div className="crm-drawer">
            <div className="crm-drawer-head">
              <div>
                <h3>{detail.client.name}</h3>
                <div className="crm-drawer-sub">
                  {detail.client.phone && <span>📱 {detail.client.phone} &nbsp;</span>}
                  {detail.client.email && <span>✉️ {detail.client.email}</span>}
                </div>
              </div>
              <button className="crm-drawer-close" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="crm-drawer-body">
              <div className="crm-drawer-section">
                <h4 className="crm-section-title">
                  Details
                  <button className="crm-link-btn" onClick={() => openEdit(detail.client)}>Edit</button>
                </h4>
                <div className="crm-kv"><span className="k">City</span><span className="v">{detail.client.city || "—"}</span></div>
                <div className="crm-kv"><span className="k">Address</span><span className="v">{detail.client.address || "—"}</span></div>
                <div className="crm-kv"><span className="k">Source</span><span className="v">{detail.client.source || "—"}</span></div>
                {detail.client.notes && (
                  <div className="crm-kv"><span className="k">Notes</span><span className="v">{detail.client.notes}</span></div>
                )}
              </div>

              <div className="crm-drawer-section">
                <h4 className="crm-section-title">Leads & bookings</h4>
                {detail.leads.length === 0 ? (
                  <div className="crm-empty" style={{ padding: "14px 8px" }}>
                    No leads for this client yet.
                  </div>
                ) : (
                  detail.leads.map((l) => {
                    const st = stageInfo(l.stage);
                    return (
                      <div key={l.id} className="crm-list-row">
                        <div className="crm-row-main">
                          <div className="crm-row-title">
                            {l.event_type || "Enquiry"}
                            {l.event_name ? ` → 🎉 ${l.event_name}` : ""}
                          </div>
                          <div className="crm-row-sub">
                            {formatDate(l.event_date) || "No date"}
                            {formatMoney(l.quoted_amount) ? ` · ${formatMoney(l.quoted_amount)}` : ""}
                          </div>
                        </div>
                        <span className="crm-badge" style={{ background: `${st.color}22`, color: st.color }}>
                          {st.icon} {st.label}
                        </span>
                      </div>
                    );
                  })
                )}
                <div style={{ textAlign: "right", marginTop: 6 }}>
                  <button className="crm-link-btn" onClick={() => navigate("/crm/leads?new=1")}>
                    ＋ New lead for a client →
                  </button>
                </div>
              </div>

              <div className="crm-drawer-section">
                <h4 className="crm-section-title">Timeline</h4>
                <form className="crm-activity-form" onSubmit={addActivity}>
                  <select value={actType} onChange={(e) => setActType(e.target.value)}>
                    {ACTIVITY_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>{t.icon} {t.label}</option>
                    ))}
                  </select>
                  <input
                    value={actSummary}
                    onChange={(e) => setActSummary(e.target.value)}
                    placeholder="Log a call, chat or note…"
                  />
                  <button className="crm-btn primary" type="submit">Log</button>
                </form>
                {detail.activities.length === 0 ? (
                  <div className="crm-empty" style={{ padding: "14px 8px" }}>
                    Nothing logged yet.
                  </div>
                ) : (
                  <ul className="crm-timeline" style={{ marginTop: 10 }}>
                    {detail.activities.map((a) => (
                      <li key={a.id}>
                        <span className="t-icon">{activityInfo(a.type).icon}</span>
                        <div className="t-body">
                          <div className="t-summary">{a.summary}</div>
                          <div className="t-when">{formatDateTime(a.activity_date)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="crm-drawer-section" style={{ textAlign: "right" }}>
                <button className="crm-btn danger" onClick={() => deleteClient(detail.client)}>
                  Delete client
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CrmClients;
